import { z } from 'zod'

export const quadrantSchema = z.enum([
  'operational_edge',
  'accelerate',
  'monitor',
  'emergency_upgrade',
])

export type Quadrant = z.infer<typeof quadrantSchema>

export const QUADRANT_ORDER: readonly Quadrant[] = [
  'operational_edge',
  'accelerate',
  'monitor',
  'emergency_upgrade',
]

export const QUADRANT_LABEL: Record<Quadrant, string> = {
  operational_edge: 'Operational edge',
  accelerate: 'Accelerate',
  monitor: 'Monitor',
  emergency_upgrade: 'Emergency upgrade',
}

export const QUADRANT_DESCRIPTION: Record<Quadrant, string> = {
  operational_edge: 'High def · high readiness',
  accelerate: 'Low def · high readiness',
  monitor: 'High def · low readiness',
  emergency_upgrade: 'Low def · low readiness',
}

export const QUADRANT_COLOR: Record<Quadrant, string> = {
  operational_edge: 'var(--color-green-400)',
  accelerate: 'var(--color-amber-400)',
  monitor: 'var(--color-blue-400)',
  emergency_upgrade: 'var(--color-red-400)',
}

export const QUADRANT_BACKGROUND: Record<Quadrant, string> = {
  operational_edge: 'var(--color-green-50)',
  accelerate: 'var(--color-amber-50)',
  monitor: 'var(--color-blue-50)',
  emergency_upgrade: 'var(--color-red-50)',
}

export const UNSCORED_COLOR = 'var(--color-text-3)'
export const UNSCORED_BACKGROUND = 'var(--color-gray-100)'

export const URGENCY_CRITICAL = 65

export const URGENCY_ELEVATED = 35

export function urgencyColor(urgency: number): string {
  if (urgency >= URGENCY_CRITICAL) return 'var(--color-red-400)'
  if (urgency >= URGENCY_ELEVATED) return 'var(--color-amber-400)'
  return 'var(--color-green-400)'
}

export const DOT_SIZE_MIN = 24

export const DOT_SIZE_MAX = 40

export function dotSize(urgency: number): number {
  const scale = Math.min(Math.max(urgency, 0), 100) / 100

  return Math.round(DOT_SIZE_MIN + scale * (DOT_SIZE_MAX - DOT_SIZE_MIN))
}

export function initialsOf(name: string | null | undefined): string {
  if (!name) return '—'

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => Array.from(word)[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return initials === '' ? '—' : initials
}

export function subscoreColor(subscore: number): string {
  if (subscore >= 4) return 'var(--color-green-400)'
  if (subscore === 3) return 'var(--color-amber-400)'
  return 'var(--color-red-400)'
}

export function subscoreBackground(subscore: number): string {
  if (subscore >= 4) return 'var(--color-green-50)'
  if (subscore === 3) return 'var(--color-amber-50)'
  return 'var(--color-red-50)'
}

export const recommendationPrioritySchema = z.enum(['critical', 'high', 'moderate', 'low'])

export type RecommendationPriority = z.infer<typeof recommendationPrioritySchema>

export const PRIORITY_TAG_CLASS: Record<RecommendationPriority, string> = {
  critical: 'canopy-tag canopy-tag-critical',
  high: 'canopy-tag canopy-tag-high',
  moderate: 'canopy-tag canopy-tag-moderate',
  low: 'canopy-tag canopy-tag-low',
}

export const PRIORITY_COLOR: Record<RecommendationPriority, string> = {
  critical: 'var(--color-red-400)',
  high: 'var(--color-amber-400)',
  moderate: 'var(--color-blue-400)',
  low: 'var(--color-green-400)',
}

export function priorityOf(value: unknown): RecommendationPriority {
  const parsed = recommendationPrioritySchema.safeParse(value)
  return parsed.success ? parsed.data : 'moderate'
}
